/**
 * SVG political-compass chart.
 *
 * Draws the classic four-quadrant compass, the trajectory through the entries
 * (chronological order) and one dot per entry.
 *
 * The chart is not a control: entries are edited in their rows below, so there
 * is no point selection, no keyboard handling and no `role="button"` here.
 * Three pointer conveniences sit on top of the picture: a stationary tap on open
 * plot proposes a new point, a stationary tap on a dot opens that entry for
 * editing, and a drag on a dot moves it. They are reported through
 * `onPlacePoint`/`onEditPoint`/`onMovePoint`; the caller owns the data.
 * Hovering a point opens the tooltip for the nearest dot within the hit radius,
 * resolved by the same lookup the drag uses.
 *
 * On a touch screen a drag on a dot is additionally guarded by non-passive
 * `touchstart`/`touchmove` listeners on the SVG. `touch-action: none` is the
 * declaration that should stop the page panning, but it is not honoured for SVG
 * content everywhere, and React's own touch handlers are passive at the root, so
 * `preventDefault()` there does nothing. The listeners only suppress the browser's
 * pan; the pointer state machine above is still what moves a dot.
 *
 * Styling rule: everything the standalone exported SVG needs — fills, strokes,
 * fonts, `paint-order` — is a presentation attribute, an inline `style` or an
 * inline `<style>` inside the SVG. Page CSS (`src/styles/chart.css`) only styles
 * the HTML wrapper and the tooltip, which are never part of the export.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactElement, Ref } from 'react'

import {
  axisTicks,
  createGeometry,
  cubicPointAt,
  formatCoords,
  fromPixel,
  snapCoord,
  sortChronologically,
  toPixel,
  trajectoryPath,
  trajectorySegments,
} from '../lib/compass'
import type { TrajectorySegment } from '../lib/compass'
import { AXIS_LABELS, PALETTE, QUADRANT_LABELS, quadrantOf } from '../lib/palette'
import type { QuadrantKey } from '../lib/palette'
import type { ChartGeometry, Entry, Point2D } from '../lib/types'
import { UNMEASURED_WIDTH, useElementWidth } from './useElementWidth'
import '../styles/chart.css'

/** Square side length of the chart at its nominal (largest) size, in SVG user units. */
export const CHART_SIZE = 720

/**
 * Smallest square side length the chart lays itself out at, in SVG user units.
 *
 * A sanity bound on the geometry, not a legibility device. The geometry AND the
 * labels are in SVG user units and the component lays the viewBox out at the
 * container's measured width, so one user unit is one CSS pixel at any width: a
 * 12-unit label renders at 12px on a 390px phone exactly as on a 1440px
 * desktop. There is therefore nothing to protect by keeping the chart wide, and
 * it must stay well below a phone's width, because a floor the container cannot
 * reach is the only way the SVG ends up rendered below 1:1 — which is what
 * scaled every label down and forced a sideways pan. Label crowding is handled
 * by {@link tickLabelSize}, {@link axisTitleSize} and {@link quadrantLabelSize}
 * instead.
 */
export const MIN_CHART_SIZE = 280

/**
 * Gap between the SVG edge and the plot area at {@link CHART_SIZE}, in SVG user
 * units. Kept proportional at other sizes by {@link chartPadding}.
 */
export const CHART_PADDING = 64

/** Stroke width of the trajectory, in SVG user units. */
const TRAJECTORY_STROKE_WIDTH = 3

/**
 * `id` of the clip path that keeps the trajectory inside the plot area. A single
 * module-level constant so the `<clipPath>` definition and the two `clip-path`
 * references cannot drift apart.
 */
const PLOT_CLIP_ID = 'compass-plot-clip'

/**
 * Arrowhead geometry, relative to the dot it points into.
 *
 * The tip sits one dot radius plus {@link ARROW_GAP} back from the final dot's
 * centre, so it points into the dot rather than being buried by it. The length
 * is then capped at the `room` actually left between the two dots (see
 * {@link trajectoryArrowHead}), so the base can never land inside the previous
 * dot. A hop with no room for a readable arrowhead (less than one dot radius)
 * gets no arrowhead at all rather than a stub.
 *
 * The two ratios set a small, sharp head: 1.7 dot radii (about 11 units) long
 * with a half-width of 0.5 of that, so the base edge is as wide as the head is
 * long. The previous 2.4 by 0.55 drew a 17-unit-wide wedge, nearly as wide as
 * the dot, which on a turning hop read as a piece of the chord laid over the
 * curve rather than as an arrowhead.
 */
const ARROW_LENGTH_RATIO = 1.7
const ARROW_HALF_WIDTH_RATIO = 0.5
const ARROW_GAP = 2

/**
 * Steps used to measure arc length along the final segment when orienting the
 * arrowhead. 64 equal steps of the Bézier parameter place the sampled point
 * within a fraction of a pixel of the true arc-length position at every chart
 * size, which is far finer than a 17-unit-wide arrowhead can show.
 */
const ARROW_ARC_STEPS = 64

/** Font stack shared by every text node, in the chart and in the export. */
const FONT_FAMILY = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"

/**
 * Inline SVG stylesheet: the export has no page CSS, so anything the SVG needs
 * must travel with it. The font stack and `paint-order` are presentation
 * attributes on every element that uses them, so a rule repeating them here
 * would only be a second source of truth to drift.
 */
const SVG_STYLE = 'text { text-rendering: geometricPrecision; }'

/** Widest the tooltip may get before it would overflow a narrow card. */
export const TOOLTIP_MAX_WIDTH = 'min(17rem, calc(50% - 12px))'

/**
 * Dot radius, in SVG user units. Kept a little under the previous 7 so the
 * trajectory reads as the primary mark rather than a chain of blobs — the curve
 * is the thing that carries the story here.
 */
const POINT_RADIUS = 6.5
const POINT_STROKE_WIDTH = 2.5

/**
 * Compass step a placed or dragged coordinate lands on: a half unit.
 *
 * The tap and the drag share it, so a point placed on empty plot and one nudged
 * with the pointer end up on the same grid.
 */
export const PLACEMENT_SNAP = 0.5

/**
 * Radius of the invisible pointer hit area around a dot, in SVG user units.
 *
 * The visible dot is only `POINT_RADIUS` (6.5) units across, which is not an
 * honest finger target, so this larger circle is the one a pointer actually
 * lands on. It is stripped from the export.
 */
export const POINT_HIT_RADIUS = 20

/** Pointer travel below this many CSS pixels is still a stationary tap. */
export const TAP_SLOP_PX = 10

/** A press held at least this long is not a tap. */
export const TAP_MAX_MS = 500

const GRID_OPACITY = 0.22
const AXIS_OPACITY = 0.75
const CORNER_INSET = 14

/**
 * Offsets of the labels that sit OUTSIDE the plot, as fractions of the padding.
 *
 * The padding is the margin that scales with the chart ({@link chartPadding}),
 * and these labels are drawn in it, so their offsets have to be fractions of it
 * too: a fixed 56 units below the plot sits comfortably inside a 720 chart and
 * 21 units past the end of a 390 one, which is what put "Libertarian" outside the
 * viewBox on a phone. Each fraction is chosen so the value at {@link CHART_SIZE}
 * is exactly the fixed offset the chart was designed with — 34, 8, 20, 20 and
 * 12 — which is what keeps the exported 720 payload byte-identical.
 */
const AXIS_TITLE_TOP_RATIO = 34 / 64
const AXIS_TITLE_BOTTOM_RATIO = 8 / 64
const AXIS_TITLE_EDGE_RATIO = 20 / 64
const TICK_LABEL_BASELINE_RATIO = 20 / 64
const TICK_LABEL_EDGE_RATIO = 12 / 64

/**
 * Ascent of the tallest glyph as a fraction of the font size, for the fallback
 * font stack. It is only needed by the two rotated side titles, whose ascenders
 * spread TOWARDS the SVG edge: a baseline `padding * 20/64` from that edge is
 * still clipped if the gap is shorter than one ascender, which is the case at
 * {@link MIN_CHART_SIZE} (7.78 units of gap against the 12px font floor's 9 units
 * of ascent). Inert at {@link CHART_SIZE}, where the fraction gives 20 against an
 * ascent of 12.
 */
const GLYPH_ASCENT_RATIO = 0.75

/** Label sizes at {@link CHART_SIZE}, i.e. the sizes the chart was designed at. */
const TICK_LABEL_SIZE = 12
const AXIS_TITLE_SIZE = 16
const QUADRANT_LABEL_SIZE = 14
/** Floors for the same three labels on a phone-sized chart. */
const TICK_LABEL_FONT_MIN = 10
const AXIS_TITLE_FONT_MIN = 12
const QUADRANT_LABEL_FONT_MIN = 11

/**
 * Smallest chart side length that still draws the in-chart quadrant
 * descriptors.
 *
 * The longest descriptor is "Authoritarian Right": 19 characters, which at the
 * 11px floor is about 115px wide at the usual ~0.55em per character. A
 * quadrant's inner width, `(size - 2 * padding) / 2 - CORNER_INSET`, falls below
 * that around size 310 (about 113px), and is about 126px at 340, so 340 leaves a
 * margin. Losing the descriptors costs nothing below it: the header's quadrant
 * key already names all four, and this is only reachable on a container
 * narrower than a normal phone anyway.
 */
export const QUADRANT_LABEL_MIN_SIZE = 340

/** Vertical offset of the year label below its dot: `dotY + radius + 13`. */
const YEAR_LABEL_DY = 13

/** Two decimals, so a `font-size` attribute stays tidy (`11.67`, not `11.666667`). */
function round2(value: number): number {
  return Math.round(value * 100) / 100
}

/** `value` held inside `[min, max]`; the ceiling wins if they ever cross. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Convert a pointer's client position into SVG user units, or `null` when the
 * chart has no measurable box.
 *
 * One CSS pixel is not always one SVG user unit: `max-width: 100%` lets the
 * browser scale the SVG down, so the element's own bounding rect supplies the
 * mapping. A zero-size rect — jsdom reports one for every element, and a chart
 * that has not been laid out reports one for real — has no mapping at all:
 * scaling by it collapses every client position onto the origin, which would
 * fabricate a coordinate. Returning `null` lets the caller abandon the gesture
 * instead, so a tap or a drag it cannot honour calls nothing back.
 */
function clientToUser(
  clientX: number,
  clientY: number,
  rect: DOMRect,
  size: number,
): Point2D | null {
  if (!(rect.width > 0) || !(rect.height > 0)) return null
  return {
    x: ((clientX - rect.left) * size) / rect.width,
    y: ((clientY - rect.top) * size) / rect.height,
  }
}

/**
 * `setPointerCapture` is an optional convenience and jsdom does not implement
 * it, so it is feature-guarded and the throw is swallowed: the gesture still
 * works without the capture.
 */
function trySetPointerCapture(element: SVGSVGElement, pointerId: number): void {
  if (typeof element.setPointerCapture !== 'function') return
  try {
    element.setPointerCapture(pointerId)
  } catch {
    // Capture is optional; dragging still works from plain pointer events.
  }
}

/** Guarded counterpart to {@link trySetPointerCapture}. */
function tryReleasePointerCapture(element: SVGSVGElement, pointerId: number): void {
  if (typeof element.releasePointerCapture !== 'function') return
  try {
    element.releasePointerCapture(pointerId)
  } catch {
    // Nothing to release, or capture was never taken.
  }
}

/** The pointer gesture in progress, tracked in a ref so moves do not re-render. */
interface PointerGesture {
  pointerId: number
  startClientX: number
  startClientY: number
  startTime: number
  /** Entry under the press, or `null` for empty plot. */
  entryId: string | null
  /** Set once the pointer travels past {@link TAP_SLOP_PX}. */
  exceeded: boolean
  /** Set when a drag has nothing to move: the gesture then calls nothing. */
  abandoned: boolean
}

/**
 * Tick and year label size for a chart `size` user units wide.
 *
 * Exactly {@link TICK_LABEL_SIZE} at {@link CHART_SIZE}, sloping down with the
 * chart and flooring at a readable size rather than staying fixed: the point of
 * a smaller chart is fewer pixels, and what a smaller chart actually needs is
 * for its labels to take up less room in each quadrant.
 */
export function tickLabelSize(size: number): number {
  return round2(clamp(size / 60, TICK_LABEL_FONT_MIN, TICK_LABEL_SIZE))
}

/** Axis title size for a chart `size` user units wide; see {@link tickLabelSize}. */
export function axisTitleSize(size: number): number {
  return round2(clamp(size / 45, AXIS_TITLE_FONT_MIN, AXIS_TITLE_SIZE))
}

/** Quadrant descriptor size for a chart `size` user units wide; see {@link tickLabelSize}. */
export function quadrantLabelSize(size: number): number {
  return round2(
    clamp((size * QUADRANT_LABEL_SIZE) / CHART_SIZE, QUADRANT_LABEL_FONT_MIN, QUADRANT_LABEL_SIZE),
  )
}

export interface CompassChartProps {
  entries: Entry[]
  /**
   * Square SVG side length, in SVG user units. Defaults to the measured width
   * of the chart wrapper clamped to `[MIN_CHART_SIZE, CHART_SIZE]`, or to
   * {@link CHART_SIZE} when no measurement is available.
   */
  size?: number
  /** Editor attaches this to hand the SVG to the PNG export. */
  svgRef?: Ref<SVGSVGElement>
  /**
   * Called with the snapped, clamped compass coordinate of a stationary tap on
   * plot with no dot under it. The caller decides what the tap means (App asks
   * for the year before creating anything). A tap on a dot calls
   * {@link CompassChartProps.onEditPoint} instead and never this.
   */
  onPlacePoint?: (x: number, y: number) => void
  /**
   * Called with an entry id when a stationary tap lands within
   * {@link POINT_HIT_RADIUS} of that dot, so the caller can open it for
   * editing. With this prop absent a tap on a dot calls nothing at all — it must
   * never fall through to `onPlacePoint` and add a point under the finger.
   */
  onEditPoint?: (id: string) => void
  /**
   * Called on each animation frame while a dot is dragged, with its id and the
   * snapped, clamped compass coordinate under the pointer.
   */
  onMovePoint?: (id: string, x: number, y: number) => void
  className?: string
}

/**
 * Plot padding for a chart `size` user units wide.
 *
 * Scaled from the 64/720 ratio the chart was designed with, so a smaller chart
 * keeps the same proportions instead of crowding its plot. `720 * CHART_PADDING
 * / CHART_SIZE` is exactly 64, so the nominal geometry is untouched.
 */
function chartPadding(size: number): number {
  return (size * CHART_PADDING) / CHART_SIZE
}

/**
 * Side length of the drawn SVG, in user units.
 *
 * An explicit `size` wins, because tests and any embedding caller want a fixed
 * chart. Otherwise the chart is laid out at the measured width of its wrapper,
 * clamped to `[MIN_CHART_SIZE, CHART_SIZE]`. That is what keeps one user unit
 * about one CSS pixel, so the labels render at their nominal pixel size instead
 * of shrinking with the canvas — and it is why the SVG's width/height
 * ATTRIBUTES are now allowed to vary with layout (the export and the tooltip
 * read those attributes back).
 *
 * The measurement is 0 wherever `ResizeObserver` is unavailable, in which case
 * the nominal size is the deterministic fallback.
 */
function resolveChartSize(size: number | undefined, measuredWidth: number): number {
  if (size !== undefined) return size
  if (measuredWidth === UNMEASURED_WIDTH) return CHART_SIZE
  return Math.min(CHART_SIZE, Math.max(MIN_CHART_SIZE, Math.round(measuredWidth)))
}

interface QuadrantRect {
  key: QuadrantKey
  x: number
  y: number
  width: number
  height: number
}

/**
 * The four quadrants as plot rects in SVG pixels. `y` grows downward, so the
 * authoritarian (`y > 0`) quadrants are the TOP half.
 */
function quadrantRects(geo: ChartGeometry): QuadrantRect[] {
  const half = geo.plot / 2
  const left = geo.padding
  const top = geo.padding
  const right = geo.padding + half
  const bottom = geo.padding + half
  return [
    { key: 'authLeft', x: left, y: top, width: half, height: half },
    { key: 'authRight', x: right, y: top, width: half, height: half },
    { key: 'libLeft', x: left, y: bottom, width: half, height: half },
    { key: 'libRight', x: right, y: bottom, width: half, height: half },
  ]
}

/**
 * Where each quadrant descriptor sits: the outer corner of its own rect, with
 * the text anchored so it grows inward.
 *
 * An explicit table keyed by `QuadrantKey` (rather than sniffing the key string)
 * so renaming or adding a quadrant is a type error instead of a silently
 * mis-placed label.
 */
const QUADRANT_LABEL_PLACEMENT: Record<
  QuadrantKey,
  { corner: 'top' | 'bottom'; align: 'start' | 'end' }
> = {
  authLeft: { corner: 'top', align: 'start' },
  authRight: { corner: 'top', align: 'end' },
  libLeft: { corner: 'bottom', align: 'start' },
  libRight: { corner: 'bottom', align: 'end' },
}

/**
 * Outer-corner position + anchor for a quadrant descriptor, kept inside the plot.
 *
 * `fontSize` is the already-resolved descriptor size (`quadrantLabelSize(size)`),
 * not the module constant: it is the baseline offset of the top-corner label, so
 * a placement that read the nominal 14 while the text was drawn at 11 would sit
 * two units off.
 */
function quadrantLabelPlacement(
  key: QuadrantKey,
  geo: ChartGeometry,
  fontSize: number,
): { x: number; y: number; textAnchor: 'start' | 'end' } {
  const { corner, align } = QUADRANT_LABEL_PLACEMENT[key]
  return {
    x: align === 'start' ? geo.padding + CORNER_INSET : geo.size - geo.padding - CORNER_INSET,
    y:
      corner === 'top'
        ? geo.padding + CORNER_INSET + fontSize
        : geo.size - geo.padding - CORNER_INSET,
    textAnchor: align,
  }
}

/**
 * Screen-reader label for a plotted entry, sharing the app's coordinate format.
 *
 * The chart's axis titles and quadrant descriptors are decorative and hidden
 * from assistive technology, so the quadrant name is folded into the label to
 * keep the point meaningful on its own.
 */
function pointAriaLabel(entry: Entry): string {
  const quadrant = QUADRANT_LABELS[quadrantOf(entry.x, entry.y)]
  const base = `${entry.year}: ${formatCoords(entry.x, entry.y)}, ${quadrant}`
  return entry.description ? `${base} — ${entry.description}` : base
}

/**
 * Point on `segment` lying `distanceFromEnd` of ARC LENGTH back from its end.
 *
 * The cubic is sampled at {@link ARROW_ARC_STEPS} equal steps of `t` and the
 * point is interpolated inside the step that straddles the target, because `t`
 * is not arc length: a cubic does not travel at constant speed. A target longer
 * than the segment clamps to its start, which the arrowhead cannot hit — the
 * room rule keeps its span shorter than the final hop's chord, and therefore
 * shorter than the curve.
 */
function pointBackAlongCurve(segment: TrajectorySegment, distanceFromEnd: number): Point2D {
  let previous = cubicPointAt(segment, 1)
  let travelled = 0
  for (let step = 1; step <= ARROW_ARC_STEPS; step += 1) {
    const point = cubicPointAt(segment, 1 - step / ARROW_ARC_STEPS)
    const chunk = Math.hypot(point.x - previous.x, point.y - previous.y)
    if (travelled + chunk >= distanceFromEnd) {
      const fraction = chunk === 0 ? 0 : (distanceFromEnd - travelled) / chunk
      return {
        x: previous.x + (point.x - previous.x) * fraction,
        y: previous.y + (point.y - previous.y) * fraction,
      }
    }
    travelled += chunk
    previous = point
  }
  return previous
}

/**
 * Points for the arrowhead that marks the newest entry, or `null` when there is
 * no direction to show.
 *
 * Callers pass the chronological order (the same array `trajectoryPath` gets).
 * The tip sits at the final dot's edge, so the arrow points into the dot
 * instead of being painted over by it.
 *
 * The arrow's axis is the direction of the CURVE it covers, not the final
 * segment's endpoint tangent: it runs from the curve point one full span (dot
 * radius, tip gap and arrow length) of arc length back to the final dot. The
 * endpoint tangent happens to be the chord between the last two dots, so an
 * arrow built from it lies along the chord and visibly cuts the corner whenever
 * the final hop is still turning — a kink and an offset that grow with the
 * arrow's length. Measuring back along the curve tilts the head with the hop
 * instead. On a straight tail the two are identical, so nothing regresses
 * there. One pass is enough: the span is taken from the chord, as it was before.
 *
 * The arrow's length is not a fraction of the hop: it is the space actually
 * available between the two dots. `room` is what is left of the hop after the
 * two dot radii and their strokes and the tip's gap are taken out, and the
 * length is capped at it, so however short the hop the base stops at (or before)
 * the previous dot's edge instead of ending up inside it. When that space is
 * smaller than a dot radius there is no room for a readable arrowhead and the
 * arrow is dropped — a short final hop simply has none. Two entries at identical
 * coordinates have no direction, and therefore no room, so they are dropped the
 * same way rather than emitting a degenerate polygon.
 */
export function trajectoryArrowHead(entries: Entry[], geo: ChartGeometry): string | null {
  if (entries.length < 2) return null

  const previous = toPixel(entries[entries.length - 2].x, entries[entries.length - 2].y, geo)
  const last = toPixel(entries[entries.length - 1].x, entries[entries.length - 1].y, geo)
  const dx = last.x - previous.x
  const dy = last.y - previous.y
  const distance = Math.hypot(dx, dy)
  // Space between the two dots that is not covered by them (radius plus stroke
  // on each side) nor by the gap the tip keeps from the final dot. Negative
  // when the dots overlap, which also covers two entries at the same point
  // (and keeps the division below safe).
  const room = distance - (2 * POINT_RADIUS + POINT_STROKE_WIDTH) - ARROW_GAP
  if (room < POINT_RADIUS) return null

  const length = Math.min(POINT_RADIUS * ARROW_LENGTH_RATIO, room)
  const half = length * ARROW_HALF_WIDTH_RATIO

  // The curve's own direction across the span the head covers. The span is
  // always shorter than the final hop's chord (room >= one dot radius), so the
  // sampled point lies inside the final segment and this never needs to walk
  // back into an earlier one.
  const span = POINT_RADIUS + ARROW_GAP + length
  const finalSegment = trajectorySegments(entries, geo)[entries.length - 2]
  const axisStart = pointBackAlongCurve(finalSegment, span)
  const axisX = last.x - axisStart.x
  const axisY = last.y - axisStart.y
  const axisLength = Math.hypot(axisX, axisY)
  // A zero-length axis cannot happen for a hop with room, but the chord is the
  // honest fallback and it keeps the divisions safe.
  const ux = axisLength === 0 ? dx / distance : axisX / axisLength
  const uy = axisLength === 0 ? dy / distance : axisY / axisLength
  const tipX = last.x - ux * (POINT_RADIUS + ARROW_GAP)
  const tipY = last.y - uy * (POINT_RADIUS + ARROW_GAP)
  const baseX = tipX - ux * length
  const baseY = tipY - uy * length
  const normalX = -uy * half
  const normalY = ux * half
  const round = (value: number): number => Math.round(value * 100) / 100

  return [
    `${round(baseX + normalX)},${round(baseY + normalY)}`,
    `${round(tipX)},${round(tipY)}`,
    `${round(baseX - normalX)},${round(baseY - normalY)}`,
  ].join(' ')
}

/** Tooltip placement, in percentages of the (fluid) wrapper, flipping near edges. */
function tooltipStyle(pixel: Point2D, size: number): CSSProperties {
  const flipX = pixel.x > size / 2
  const flipY = pixel.y > size / 2
  const offsetX = flipX ? 'calc(-100% - 12px)' : '12px'
  const offsetY = flipY ? 'calc(-100% - 12px)' : '-50%'
  return {
    left: `${(pixel.x / size) * 100}%`,
    // Vertically clamped too: a point on the top or bottom edge anchors its
    // tooltip inside the wrapper rather than outside the canvas. The tooltip's
    // `max-height` and `overflow` (chart.css) bound how far the box itself can
    // reach from that anchor.
    top: `clamp(0px, ${(pixel.y / size) * 100}%, 100%)`,
    transform: `translate(${offsetX}, ${offsetY})`,
    // Flipping alone cannot save the tooltip on a narrow card. The clamp is
    // half the wrapper minus the 12px gap, which is exactly the room left on
    // either side of the 50% flip point, so the tooltip can never overflow.
    maxWidth: TOOLTIP_MAX_WIDTH,
    pointerEvents: 'none',
  }
}

export default function CompassChart({
  entries,
  size,
  svgRef,
  onPlacePoint,
  onEditPoint,
  onMovePoint,
  className,
}: CompassChartProps): ReactElement {
  // The tooltip is hover-only: every field it shows is editable in the entry row
  // below, so nothing needs to keep it open once the pointer leaves. A drag
  // overlays this with the dragged id so the live coordinate readout stays up.
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  // Gesture bookkeeping lives in refs: a pointer move must not re-render unless a
  // drag actually began. `draggingIdRef` mirrors the state so a move can check
  // the current drag without reading a stale render's closure.
  const gestureRef = useRef<PointerGesture | null>(null)
  const draggingIdRef = useRef<string | null>(null)
  const frameRef = useRef<number | null>(null)
  const pendingMoveRef = useRef<{ id: string; x: number; y: number } | null>(null)
  // The SVG element, so the touch guard can attach its DOM listeners to it. It
  // shares the element with the caller's `svgRef` through `setSvgElement`.
  const svgElementRef = useRef<SVGSVGElement | null>(null)
  // The wrapper the SVG fills. Measuring it is what lets the viewBox follow the
  // layout, so labels are not scaled down with the canvas on a narrow screen.
  const { ref: wrapperRef, width: measuredWidth } = useElementWidth<HTMLDivElement>()

  /**
   * Attach the internal SVG ref and forward the same element to the caller's
   * `svgRef`. The touch guard needs the element to add its own listeners, and
   * the PNG export needs it through the prop, so both have to see it.
   */
  const setSvgElement = useCallback(
    (node: SVGSVGElement | null): void => {
      svgElementRef.current = node
      if (typeof svgRef === 'function') {
        svgRef(node)
      } else if (svgRef !== null && svgRef !== undefined) {
        svgRef.current = node
      }
    },
    [svgRef],
  )

  const chartSize = resolveChartSize(size, measuredWidth)
  // Derived from the resolved size, so at CHART_SIZE they are exactly the 12/16/14
  // the chart was designed with and the exported payload is unchanged.
  const tickFontSize = tickLabelSize(chartSize)
  const axisTitleFontSize = axisTitleSize(chartSize)
  const quadrantFontSize = quadrantLabelSize(chartSize)
  const showQuadrantLabels = chartSize >= QUADRANT_LABEL_MIN_SIZE
  const geo = useMemo(() => createGeometry(chartSize, chartPadding(chartSize)), [chartSize])
  const sortedEntries = useMemo(() => sortChronologically(entries), [entries])
  const ticks = axisTicks()
  const rects = useMemo(() => quadrantRects(geo), [geo])
  // Empty below QUADRANT_LABEL_MIN_SIZE, where a 19-character descriptor no
  // longer fits its quadrant's inner width: dropping them beats overprinting
  // them on each other, and the header's quadrant key names all four already.
  const descriptorRects = showQuadrantLabels ? rects : []
  const path = useMemo(() => trajectoryPath(sortedEntries, geo), [sortedEntries, geo])
  const arrowPoints = useMemo(() => trajectoryArrowHead(sortedEntries, geo), [sortedEntries, geo])

  /** Stop the queued drag frame and drop its pending coordinate. */
  function cancelPendingMove(): void {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    pendingMoveRef.current = null
  }

  /**
   * Run the queued drag frame now, if one is pending.
   *
   * A release must not drop the last move. `cancelPendingMove` would discard the
   * coordinate from the most recent `pointermove`, so a flick whose down, move
   * and up all land inside one frame would commit nothing, and a normal drag
   * would persist a frame short of where the finger stopped. Cancelling the
   * frame first keeps the one-callback-per-frame promise: the callback runs
   * here, exactly once. The abort path keeps the plain cancel, because an
   * aborted gesture must stay silent.
   */
  function flushPendingMove(): void {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    const pending = pendingMoveRef.current
    pendingMoveRef.current = null
    if (pending !== null) onMovePoint?.(pending.id, pending.x, pending.y)
  }

  // Unmount is the one place a queued frame can outlive its chart. The refs are
  // stable, so the first render's cleanup is still the right one.
  useEffect(() => cancelPendingMove, [])

  /**
   * Whether a client position is within {@link POINT_HIT_RADIUS} of a dot, as a
   * ref so the touch guard's stable handlers can see the CURRENT chart.
   *
   * The guard's DOM listeners are attached once, on mount, so its handlers must
   * not close over the first render's geometry or entries — a point added after
   * mount would otherwise not be draggable by touch. This ref is refreshed on
   * every render and read by those handlers, and it maps and looks up the
   * position exactly as the pointer path does, so the touch target and the
   * pointer target cannot drift apart.
   */
  const touchHitsDotRef = useRef<
    (clientX: number, clientY: number, svg: SVGSVGElement) => boolean
  >(() => false)
  useEffect(() => {
    touchHitsDotRef.current = (clientX, clientY, svg) => {
      const point = clientToUser(clientX, clientY, svg.getBoundingClientRect(), chartSize)
      return point !== null && nearestEntryId(point) !== null
    }
  })

  /**
   * Hand-rolled scroll guard for a touch drag.
   *
   * `touch-action: none` (chart.css) is the declaration that should stop the page
   * panning for a gesture on a dot, but on a real phone it was observed to have no
   * effect on SVG content: the page scrolled and the drag never began. React's own
   * touch handlers are passive at the root, so `preventDefault()` inside them does
   * nothing; these listeners are attached directly, with an explicit
   * `{ passive: false }`, which is what lets the call through.
   *
   * The rule is deliberately narrow: only a touch that STARTS within
   * {@link POINT_HIT_RADIUS} of a dot is claimed, so a swipe that begins on open
   * plot still scrolls the page. The handlers only suppress the browser's pan; the
   * pointer state machine above is what actually moves a dot — one state machine,
   * not two.
   */
  useEffect(() => {
    const svg = svgElementRef.current
    if (svg === null) return

    let guardActive = false

    const handleTouchStart = (event: TouchEvent): void => {
      // The first touch of the gesture decides: once a gesture is claimed, a
      // second finger must not change the decision.
      if (guardActive) return
      const touches = event.touches
      if (touches.length === 0) return
      const touch = touches[0]
      if (!touchHitsDotRef.current(touch.clientX, touch.clientY, svg)) return
      event.preventDefault()
      guardActive = true
    }

    const handleTouchMove = (event: TouchEvent): void => {
      // A browser can re-evaluate the pan mid-gesture, so the suppression has to
      // be repeated for every move, not just the start.
      if (!guardActive) return
      event.preventDefault()
    }

    const handleTouchEnd = (event: TouchEvent): void => {
      // Only the last touch to leave ends the gesture. A second finger lifting
      // still leaves the finger that owns the claim on the surface, and clearing
      // here would let the browser claim that finger's next move as a pan
      // mid-drag — exactly the bug this guard exists to stop.
      if (event.touches.length === 0) guardActive = false
    }

    svg.addEventListener('touchstart', handleTouchStart, { passive: false })
    svg.addEventListener('touchmove', handleTouchMove, { passive: false })
    svg.addEventListener('touchend', handleTouchEnd)
    svg.addEventListener('touchcancel', handleTouchEnd)
    return () => {
      svg.removeEventListener('touchstart', handleTouchStart)
      svg.removeEventListener('touchmove', handleTouchMove)
      svg.removeEventListener('touchend', handleTouchEnd)
      svg.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [])

  /** Coalesce drag updates to at most one `onMovePoint` call per frame. */
  function scheduleMove(id: string, x: number, y: number): void {
    pendingMoveRef.current = { id, x, y }
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      const pending = pendingMoveRef.current
      pendingMoveRef.current = null
      if (pending !== null) onMovePoint?.(pending.id, pending.x, pending.y)
    })
  }

  /** The entry whose dot is nearest `point` within {@link POINT_HIT_RADIUS}. */
  function nearestEntryId(point: Point2D): string | null {
    let bestId: string | null = null
    let bestDistance = Number.POSITIVE_INFINITY
    for (const entry of sortedEntries) {
      const pixel = toPixel(entry.x, entry.y, geo)
      const distance = Math.hypot(point.x - pixel.x, point.y - pixel.y)
      if (distance <= POINT_HIT_RADIUS && distance < bestDistance) {
        bestDistance = distance
        bestId = entry.id
      }
    }
    return bestId
  }

  /** Drop the dragged-id overlay without a state update when it was never set. */
  function clearDragging(): void {
    if (draggingIdRef.current === null) return
    draggingIdRef.current = null
    setDraggingId(null)
  }

  /** End the gesture without calling back; releases capture when still held. */
  function abortGesture(svg?: SVGSVGElement): void {
    const gesture = gestureRef.current
    gestureRef.current = null
    cancelPendingMove()
    clearDragging()
    if (svg !== undefined && gesture !== null) tryReleasePointerCapture(svg, gesture.pointerId)
  }

  /** The SVG user-space point under a pointer, or `null` when there is no box. */
  function pointerToUser(event: ReactPointerEvent<SVGSVGElement>): Point2D | null {
    return clientToUser(
      event.clientX,
      event.clientY,
      event.currentTarget.getBoundingClientRect(),
      chartSize,
    )
  }

  function handlePointerDown(event: ReactPointerEvent<SVGSVGElement>): void {
    // Only a primary, left-button press starts a gesture: a right or middle
    // click (or a secondary touch) must neither drag a point nor open the year
    // dialog.
    if (!event.isPrimary || event.button !== 0) return

    // A second pointer down is a multi-touch gesture: end the first with no
    // callbacks rather than letting two drags fight over the same state.
    if (gestureRef.current !== null) {
      abortGesture(event.currentTarget)
      return
    }
    // A press that cannot be mapped starts no gesture at all, so its move and
    // release cannot call anything back either.
    const point = pointerToUser(event)
    if (point === null) return
    gestureRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startTime: Date.now(),
      entryId: nearestEntryId(point),
      exceeded: false,
      abandoned: false,
    }
    trySetPointerCapture(event.currentTarget, event.pointerId)
  }

  function handlePointerMove(event: ReactPointerEvent<SVGSVGElement>): void {
    // Hover and drag share one lookup on purpose: the hit circles are painted in
    // order and a later, larger circle can cover an earlier dot, so a per-group
    // `mouseenter` reported whichever circle painted last and showed the wrong
    // entry's tooltip for nearby dots.
    const point = pointerToUser(event)
    if (point === null) {
      setHoveredId(null)
    } else {
      const nearest = nearestEntryId(point)
      setHoveredId((current) => (current === nearest ? current : nearest))
    }

    const gesture = gestureRef.current
    if (gesture === null || event.pointerId !== gesture.pointerId || gesture.abandoned) return

    if (!gesture.exceeded) {
      const dx = event.clientX - gesture.startClientX
      const dy = event.clientY - gesture.startClientY
      if (Math.hypot(dx, dy) <= TAP_SLOP_PX) return
      gesture.exceeded = true
    }

    // No dot under the press, nowhere to report a move, or a box that stopped
    // being measurable: this gesture is a scroll, so from here on it must never
    // call anything.
    if (gesture.entryId === null || onMovePoint === undefined || point === null) {
      gesture.abandoned = true
      return
    }

    const compass = fromPixel(point, geo)
    if (draggingIdRef.current !== gesture.entryId) {
      draggingIdRef.current = gesture.entryId
      setDraggingId(gesture.entryId)
    }
    scheduleMove(
      gesture.entryId,
      snapCoord(compass.x, PLACEMENT_SNAP),
      snapCoord(compass.y, PLACEMENT_SNAP),
    )
  }

  function handlePointerUp(event: ReactPointerEvent<SVGSVGElement>): void {
    const gesture = gestureRef.current
    if (gesture === null || event.pointerId !== gesture.pointerId) return
    gestureRef.current = null
    // Flush before the drag/tap branch: the release lands any queued move, so
    // the last coordinate is never lost.
    flushPendingMove()
    clearDragging()
    tryReleasePointerCapture(event.currentTarget, event.pointerId)

    // A drag already committed every move, and a slow press is not a tap.
    if (gesture.abandoned || gesture.exceeded) return
    if (Date.now() - gesture.startTime >= TAP_MAX_MS) return

    // A press that landed on a dot opens that entry for editing, whatever the
    // tap would otherwise have placed. With no edit handler the tap simply does
    // nothing: adding a point under the dot is the opposite of what the finger
    // aimed at.
    if (gesture.entryId !== null) {
      onEditPoint?.(gesture.entryId)
      return
    }
    if (onPlacePoint === undefined) return

    const point = pointerToUser(event)
    if (point === null) return
    const compass = fromPixel(point, geo)
    onPlacePoint(snapCoord(compass.x, PLACEMENT_SNAP), snapCoord(compass.y, PLACEMENT_SNAP))
  }

  function handlePointerLeave(): void {
    // Hover only: a pointer that left the chart is over no dot. A drag with
    // pointer capture keeps its own `draggingId`, so this cannot close a held
    // open tooltip mid-drag.
    setHoveredId(null)
  }

  function handlePointerCancel(event: ReactPointerEvent<SVGSVGElement>): void {
    const gesture = gestureRef.current
    if (gesture === null || event.pointerId !== gesture.pointerId) return
    abortGesture(event.currentTarget)
  }

  function handleLostPointerCapture(event: ReactPointerEvent<SVGSVGElement>): void {
    const gesture = gestureRef.current
    if (gesture === null || event.pointerId !== gesture.pointerId) return
    abortGesture()
  }

  // A drag holds the dragged point's tooltip open as the live readout.
  const activeId = draggingId ?? hoveredId
  const activeEntry =
    activeId === null ? undefined : sortedEntries.find((entry) => entry.id === activeId)

  const plotRight = geo.size - geo.padding
  const plotBottom = geo.size - geo.padding
  // The labels drawn in the padding, all placed at a fraction of it (see the
  // *_RATIO constants above). The two side titles are the one exception: they
  // are rotated, so their ascenders reach towards the edge they sit next to and
  // the offset has to clear one glyph as well as the nominal fraction.
  const tickBaselineY = plotBottom + geo.padding * TICK_LABEL_BASELINE_RATIO
  const tickLabelX = geo.padding - geo.padding * TICK_LABEL_EDGE_RATIO
  const titleTopY = geo.padding * AXIS_TITLE_TOP_RATIO
  const titleBottomY = geo.size - geo.padding * AXIS_TITLE_BOTTOM_RATIO
  const titleLeftX = Math.max(
    geo.padding * AXIS_TITLE_EDGE_RATIO,
    axisTitleFontSize * GLYPH_ASCENT_RATIO,
  )
  const titleRightX = geo.size - titleLeftX
  const gridStroke = {
    stroke: PALETTE.grid,
    strokeOpacity: GRID_OPACITY,
    strokeWidth: 1,
  }
  const axisStroke = {
    stroke: PALETTE.grid,
    strokeOpacity: AXIS_OPACITY,
    strokeWidth: 2,
  }

  return (
    <div className={className ? `compass-chart ${className}` : 'compass-chart'} ref={wrapperRef}>
      <svg
        id="compass-chart"
        data-testid="compass-chart"
        ref={setSvgElement}
        className="compass-chart__svg"
        width={chartSize}
        height={chartSize}
        viewBox={`0 0 ${chartSize} ${chartSize}`}
        role="group"
        aria-label={`Political compass chart with ${sortedEntries.length} plotted ${
          sortedEntries.length === 1 ? 'belief' : 'beliefs'
        } over time`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
        onLostPointerCapture={handleLostPointerCapture}
      >
        <style>{SVG_STYLE}</style>

        {/*
          The trajectory is data and belongs inside the data area. At a tension
          above 1 the control points overshoot a corner-to-corner hop, so without
          this clip the curve would run over the tick labels (or be cut off by
          the root viewBox), which reads as the chart being broken. The clip is
          the plot rect itself, so a legal trajectory is never visibly changed.

          The dots are deliberately NOT clipped: a point sitting on the plot
          boundary should straddle it, and a half dot there is honest about it.
        */}
        <defs>
          <clipPath id={PLOT_CLIP_ID} data-testid="compass-plot-clip">
            <rect x={geo.padding} y={geo.padding} width={geo.plot} height={geo.plot} />
          </clipPath>
        </defs>

        {/*
          Everything in this group is decorative. The root is a `group` (not an
          atomic `img`) so the point groups below stay reachable, and without
          `aria-hidden` a screen reader would read the grid, the tick numbers and
          the quadrant descriptors as a stream of loose text before ever
          reaching a point.
        */}
        <g aria-hidden="true">
        {/* Whole-canvas white so the rasterised/exported image is never transparent. */}
        <rect x={0} y={0} width={geo.size} height={geo.size} fill={PALETTE.plotBackground} />

        <rect
          data-testid="compass-plot-background"
          x={geo.padding}
          y={geo.padding}
          width={geo.plot}
          height={geo.plot}
          fill={PALETTE.plotBackground}
        />

        {/* Quadrant fills: y > 0 is the top half of the plot. */}
        {rects.map((rect) => (
          <rect
            key={rect.key}
            data-testid="compass-quadrant"
            data-quadrant={rect.key}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            fill={PALETTE[rect.key]}
          />
        ))}

        {/* Grid at every 5 compass units; the two lines through the origin are the axes. */}
        {ticks.map((tick) => {
          const vertical = toPixel(tick, 0, geo).x
          const horizontal = toPixel(0, tick, geo).y
          const isAxis = tick === 0
          return (
            <g key={tick} data-testid={isAxis ? 'compass-axis' : 'compass-gridline'}>
              <line
                x1={vertical}
                y1={geo.padding}
                x2={vertical}
                y2={plotBottom}
                {...(isAxis ? axisStroke : gridStroke)}
              />
              <line
                x1={geo.padding}
                y1={horizontal}
                x2={plotRight}
                y2={horizontal}
                {...(isAxis ? axisStroke : gridStroke)}
              />
            </g>
          )
        })}

        {/* Tick labels just outside the plot; 0 is skipped to avoid clutter. */}
        {ticks
          .filter((tick) => tick !== 0)
          .map((tick) => (
            <g key={tick}>
              <text
                data-testid="compass-tick-label"
                x={toPixel(tick, 0, geo).x}
                y={tickBaselineY}
                textAnchor="middle"
                fontFamily={FONT_FAMILY}
                fontSize={tickFontSize}
                fill={PALETTE.axisText}
              >
                {tick}
              </text>
              <text
                data-testid="compass-tick-label"
                x={tickLabelX}
                y={toPixel(0, tick, geo).y}
                dy="0.32em"
                textAnchor="end"
                fontFamily={FONT_FAMILY}
                fontSize={tickFontSize}
                fill={PALETTE.axisText}
              >
                {tick}
              </text>
            </g>
          ))}

        {/* Pole titles, one per edge. */}
        <text
          data-testid="compass-axis-title"
          x={geo.center.x}
          y={titleTopY}
          textAnchor="middle"
          fontFamily={FONT_FAMILY}
          fontSize={axisTitleFontSize}
          fontWeight={700}
          letterSpacing="0.04em"
          fill={PALETTE.axisText}
        >
          {AXIS_LABELS.top}
        </text>
        <text
          data-testid="compass-axis-title"
          x={geo.center.x}
          y={titleBottomY}
          textAnchor="middle"
          fontFamily={FONT_FAMILY}
          fontSize={axisTitleFontSize}
          fontWeight={700}
          letterSpacing="0.04em"
          fill={PALETTE.axisText}
        >
          {AXIS_LABELS.bottom}
        </text>
        <text
          data-testid="compass-axis-title"
          x={titleLeftX}
          y={geo.center.y}
          transform={`rotate(-90 ${titleLeftX} ${geo.center.y})`}
          textAnchor="middle"
          fontFamily={FONT_FAMILY}
          fontSize={axisTitleFontSize}
          fontWeight={700}
          letterSpacing="0.04em"
          fill={PALETTE.axisText}
        >
          {AXIS_LABELS.left}
        </text>
        <text
          data-testid="compass-axis-title"
          x={titleRightX}
          y={geo.center.y}
          transform={`rotate(90 ${titleRightX} ${geo.center.y})`}
          textAnchor="middle"
          fontFamily={FONT_FAMILY}
          fontSize={axisTitleFontSize}
          fontWeight={700}
          letterSpacing="0.04em"
          fill={PALETTE.axisText}
        >
          {AXIS_LABELS.right}
        </text>

        {/* Trajectory first so the dots sit on top of it. */}
        {sortedEntries.length >= 2 && (
          <path
            data-testid="compass-trajectory"
            fill="none"
            stroke={PALETTE.trajectory}
            strokeWidth={TRAJECTORY_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.85}
            clipPath={`url(#${PLOT_CLIP_ID})`}
            d={path}
          />
        )}

        {/*
          Quadrant descriptors, haloed so they stay legible on every fill.
          `descriptorRects` is empty below QUADRANT_LABEL_MIN_SIZE.
        */}
        {descriptorRects.map((rect) => {
          const placement = quadrantLabelPlacement(rect.key, geo, quadrantFontSize)
          return (
            <text
              key={rect.key}
              data-testid="compass-quadrant-label"
              data-quadrant={rect.key}
              className="compass-chart__quadrant-label"
              x={placement.x}
              y={placement.y}
              textAnchor={placement.textAnchor}
              fontFamily={FONT_FAMILY}
              fontSize={quadrantFontSize}
              fontWeight={700}
              fill={PALETTE.quadrantText}
              stroke="rgba(15, 23, 42, 0.5)"
              strokeWidth={3}
              paintOrder="stroke"
            >
              {QUADRANT_LABELS[rect.key]}
            </text>
          )
        })}
        {/* end of the decorative, aria-hidden group */}
        </g>

        {/*
          Each point is a picture of a row, not a control: no `tabindex`, no
          `role="button"` and no handlers. The year label below the dot and the
          `aria-label` carry everything a reader needs. Hover is resolved by the
          root's pointer handling (see `handlePointerMove`), not by per-group
          enter/leave handlers.
        */}
        {sortedEntries.map((entry) => {
          const pixel = toPixel(entry.x, entry.y, geo)
          return (
            <g
              key={entry.id}
              data-testid="compass-point"
              data-entry-id={entry.id}
              data-dragging={draggingId === entry.id ? 'true' : undefined}
              transform={`translate(${pixel.x} ${pixel.y})`}
              role="img"
              aria-label={pointAriaLabel(entry)}
            >
              {/*
                Invisible pointer hit target, first in the group so the dot and
                the year label paint over it: it only exists to catch a gesture
                and is stripped from the export. `pointer-events="all"` makes the
                transparent fill grabbable, and `touch-action: none`
                (chart.css) stops the browser claiming a drag on a dot as a
                scroll before the drag can begin.
              */}
              <circle
                data-testid="compass-point-hit"
                className="compass-chart__point-hit"
                r={POINT_HIT_RADIUS}
                fill="transparent"
                pointerEvents="all"
                data-export-ignore="true"
              />
              <circle
                r={POINT_RADIUS}
                fill={PALETTE.pointFill}
                stroke={PALETTE.pointStroke}
                strokeWidth={POINT_STROKE_WIDTH}
              />
              <text
                data-testid="compass-year-label"
                className="compass-chart__year"
                y={POINT_RADIUS + YEAR_LABEL_DY}
                textAnchor="middle"
                fontFamily={FONT_FAMILY}
                fontSize={tickFontSize}
                fontWeight={700}
                fill={PALETTE.axisText}
                stroke="#ffffff"
                strokeWidth={3.5}
                paintOrder="stroke"
                style={{ pointerEvents: 'none' }}
              >
                {entry.year}
              </text>
            </g>
          )
        })}

        {/*
          Above the dots, not part of the trajectory path: an arrowhead drawn as
          a `marker-end` on the line is swallowed by the final dot's fill, which
          is exactly what happened before. The tip stops at the dot's edge so it
          points *into* the newest point rather than covering it.
        */}
        {arrowPoints !== null && (
          <g aria-hidden="true">
            <polygon
              data-testid="compass-trajectory-arrow"
              points={arrowPoints}
              fill={PALETTE.trajectory}
              clipPath={`url(#${PLOT_CLIP_ID})`}
            />
          </g>
        )}
      </svg>

      {activeEntry !== undefined && (
        <div
          data-testid="compass-tooltip"
          // Purely visual: it repeats the hovered point's own `aria-label`, and
          // nothing references it with `aria-describedby`, so leaving it visible
          // to assistive technology would only add unreferenced duplicate text.
          // No `role="tooltip"` either: with `aria-hidden` it was dead weight,
          // and the point's own label already carries the information.
          aria-hidden="true"
          className="compass-chart__tooltip"
          style={tooltipStyle(toPixel(activeEntry.x, activeEntry.y, geo), chartSize)}
        >
          <strong className="compass-chart__tooltip-year">{activeEntry.year}</strong>
          <span className="compass-chart__tooltip-coords">
            {formatCoords(activeEntry.x, activeEntry.y)}
          </span>
          {activeEntry.description ? (
            <p className="compass-chart__tooltip-description">{activeEntry.description}</p>
          ) : null}
        </div>
      )}
    </div>
  )
}
